import { showGuidance } from './liveGuidance';
import { supportChatApi as api, supportChatOrigin } from '../../services/supportChatApi';

export const LIVE_CONSENT_VERSION = 2;
export const privateSelector = [
  '.eo-observation', '.eo-chat-widget', '[data-support-private]', '.rr-block',
  'object', 'embed',
  'input[type="hidden"]', 'input[type="password"]', '[autocomplete="one-time-code"]', '[autocomplete^="cc-"]', '[autocomplete*="password"]',
  '[name*="password" i]', '[name*="senha" i]', '[name*="secret" i]', '[name*="token" i]',
  '[id*="password" i]', '[id*="senha" i]', '[id*="secret" i]', '[id*="token" i]',
  '[name*="cvv" i]', '[name*="cvc" i]', '[name*="cardnumber" i]', '[name*="cartao" i]',
  '[id*="cvv" i]', '[id*="cvc" i]', '[id*="cardnumber" i]', '[id*="cartao" i]',
  '[class*="qrcode" i]', '[class*="qr-code" i]', 'img[alt*="QR" i]', '[data-support-payment]',
].join(',');
const safeUrl = value => {
  if (!value || value.startsWith('data:image/')) return value;
  try {
    const url = new URL(value, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    url.username = ''; url.password = '';
    for (const key of [...url.searchParams.keys()]) {
      if (!['v', 'version', 'w', 'h', 'width', 'height', 'format', 'quality'].includes(key)) url.searchParams.delete(key);
    }
    return url.href;
  } catch (_) { return ''; }
};
// CSS uses these attributes for visible state and generated labels. Preserve
// presentation metadata without forwarding arbitrary application data attributes.
const presentationAttributes = new Set([
  'data-state', 'data-active', 'data-selected', 'data-expanded', 'data-status',
  'data-theme', 'data-variant', 'data-placement', 'data-side', 'data-orientation',
  'data-loading-text', 'data-cancelled-label',
]);
const unsafeCanvasProperties = new Set(['__proto__', 'prototype', 'constructor', 'caller', 'callee', 'arguments']);
const sanitizeCanvasMutation = mutation => {
  if (!mutation || typeof mutation !== 'object' || Array.isArray(mutation)) return null;
  const property = mutation.property;
  // rrweb replays these names as methods or setters on a live canvas context.
  // Keep normal Canvas 2D/WebGL commands while rejecting prototype escape hatches.
  if (typeof property !== 'string' || !/^[A-Za-z][A-Za-z0-9]*$/.test(property) || unsafeCanvasProperties.has(property)) return null;
  return mutation;
};
const sanitizeCanvasEntry = entry => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const mutation = sanitizeCanvasMutation(entry.mutation);
  return mutation ? { ...entry, mutation } : null;
};
// Defense in depth: no script attributes, hidden data attributes or raw page URL.
// Privacy-sensitive DOM subtrees are blocked by the recorder before serialization.
export function sanitizeLiveEvent(event) {
  const isCanvasMutation = event?.type === 3 && event?.data?.source === 9;
  const visit = (value, canvasMutation = false) => {
    if (Array.isArray(value)) return value.map(item => visit(item, canvasMutation));
    if (!value || typeof value !== 'object') return value;
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      if (key === 'canvasMutations' && Array.isArray(item)) {
        out.canvasMutations = item.map(sanitizeCanvasEntry).filter(Boolean).map(value => visit(value));
      } else if (canvasMutation && key === 'commands' && Array.isArray(item)) {
        out.commands = item.map(sanitizeCanvasMutation).filter(Boolean).map(value => visit(value, true));
      } else if (canvasMutation && key === 'property') {
        if (typeof item === 'string' && /^[A-Za-z][A-Za-z0-9]*$/.test(item) && !unsafeCanvasProperties.has(item)) out[key] = item;
      } else if (key === 'attributes' && item && typeof item === 'object') {
        out.attributes = {};
        for (const [name, content] of Object.entries(item)) {
          if (/^on|^nonce$|^srcdoc$|^formaction$|^action$/i.test(name) ||
              (/^data-/i.test(name) && !presentationAttributes.has(name.toLowerCase()))) continue;
          out.attributes[name] = ['src', 'href', 'poster', 'xlink:href', 'rr_src'].includes(name) && typeof content === 'string' ? safeUrl(content) : content;
        }
      } else if (key === 'href' && event.type === 4) out[key] = window.location.origin;
      else out[key] = visit(item, canvasMutation);
    }
    return out;
  };
  return visit(event, isCanvasMutation);
}

async function connect(sessionId, tabId, signal) {
  const [{ io }, { data }] = await Promise.all([
    import('socket.io-client'),
    api.post(sessionId ? `/api/v1/support-chat/live/${sessionId}/connection` : '/api/v1/support-chat/presence/connection', tabId ? { tabId } : {}, { signal }),
  ]);
  if (signal.aborted) return null;
  return io(supportChatOrigin, { path: data.path, auth: { ticket: data.ticket }, transports: ['websocket'],
    reconnection: false, autoConnect: false, timeout: 10000 });
}

const MAX_PACKET = 5 * 1024 * 1024;
const MAX_QUEUE = 2 * 1024 * 1024;
const eventBytes = event => new TextEncoder().encode(JSON.stringify(event)).byteLength;
const generation = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

export function publishLivePage({ sessionId, tabId, onStatus }) {
  const controller = new AbortController();
  let socket, stopRecord, flushTimer, retryTimer, resyncTimer, clearPoint;
  let pending = [], pendingBytes = 0, busy = false, epoch = '', seq = 0, meta;
  let stalled = false, terminal = false, lastSnapshot = 0, delay = 40;
  const stopRecording = () => {
    stopRecord?.(); stopRecord = null; clearPoint?.();
    clearTimeout(flushTimer); clearTimeout(resyncTimer);
    pending = []; pendingBytes = 0; busy = false;
  };
  const run = async () => {
    try {
      const [{ record }, connection] = await Promise.all([import('@rrweb/record'), connect(sessionId, tabId, controller.signal)]);
      if (controller.signal.aborted || !connection || terminal) return;
      socket = connection;
      const resync = () => {
        if (resyncTimer || terminal) return;
        resyncTimer = setTimeout(() => {
          resyncTimer = null;
          if (busy) { resync(); return; }
          if (stopRecord && socket.connected) record.takeFullSnapshot(true);
        }, Math.max(100, 1000 - (Date.now() - lastSnapshot)));
      };
      const flush = () => {
        flushTimer = null;
        if (busy || !socket.connected || !pending.length || terminal) return;
        if (pendingBytes > MAX_PACKET) {
          terminal = true; stopRecording(); onStatus?.('capacity'); socket.disconnect(); return;
        }
        const events = pending.splice(0, 250);
        pendingBytes = pending.reduce((total, event) => total + eventBytes(event), 0);
        const packet = { epoch, seq: ++seq, events };
        const sent = performance.now(); busy = true;
        socket.timeout(2500).emit('live:batch', packet, (error, result) => {
          if (controller.signal.aborted || socket !== connection) return;
          busy = false;
          delay = Math.min(80, Math.max(30, (performance.now() - sent) / 3));
          if (error || !result?.ok || stalled) {
            pending = []; pendingBytes = 0; stalled = true;
            if (result?.reason === 'capacity' || result?.reason === 'invalid') {
              terminal = true; stopRecording(); onStatus?.('capacity'); socket.disconnect(); return;
            }
            resync();
          } else if (pending.length) flushTimer = setTimeout(flush, delay);
        });
      };
      socket.on('connect', () => {
        onStatus?.('connected'); stalled = false; busy = false;
        stopRecord = record({
          emit: event => {
            if (controller.signal.aborted || !socket.connected || ![0, 1, 2, 3, 4].includes(event.type)) return;
            if (event.type === 4) { meta = sanitizeLiveEvent(event); return; }
            if (event.type === 2) {
              // Every full snapshot starts an independent mirror generation.
              clearPoint?.(); pending = []; pendingBytes = 0;
              epoch = generation(); seq = 0; stalled = false; lastSnapshot = Date.now();
              if (meta) pending.push({ ...meta, timestamp: event.timestamp });
            } else if (stalled || !epoch) return;
            const clean = sanitizeLiveEvent(event);
            pending.push(clean); pendingBytes += eventBytes(clean);
            if ((pendingBytes > MAX_QUEUE && event.type !== 2) || pending.length > 1000) {
              pending = []; pendingBytes = 0; stalled = true; resync(); return;
            }
            clearTimeout(flushTimer);
            if (event.type === 2) flush();
            else flushTimer = setTimeout(flush, delay);
          },
          blockSelector: privateSelector, maskAllInputs: false, maskInputOptions: { password: true },
          maskTextSelector: '[data-support-mask], .rr-mask', inlineStylesheet: true,
          inlineImages: false, recordCanvas: true, recordCrossOriginIframes: false,
          keepIframeSrcFn: () => false, collectFonts: true, slimDOMOptions: true,
          checkoutEveryNms: 60000,
          sampling: { mousemove: 80, scroll: 80, media: 1000, canvas: 2 },
        });
      });
      socket.on('live:clock', reply => { if (typeof reply === 'function') reply(Date.now()); });
      socket.on('live:point', (point, reply) => {
        if (point?.epoch !== epoch || document.hidden) return reply?.({ ok: false });
        const node = record.mirror.getNode(point.id);
        clearPoint?.(); clearPoint = showGuidance(node, point.agentName, privateSelector);
        reply?.({ ok: !!clearPoint });
      });
      socket.on('live:resync', resync);
      socket.on('live:ended', () => { terminal = true; stopRecording(); onStatus?.('ended'); });
      const disconnected = () => {
        stopRecording(); connection.removeAllListeners(); connection.disconnect();
        if (!controller.signal.aborted && !terminal) { onStatus?.('disconnected'); retryTimer = setTimeout(run, 750); }
      };
      socket.on('disconnect', disconnected); socket.on('connect_error', disconnected); socket.connect();
    } catch (_) {
      if (!controller.signal.aborted && !terminal) { onStatus?.('disconnected'); retryTimer = setTimeout(run, 1500); }
    }
  };
  run();
  return () => { controller.abort(); clearTimeout(retryTimer); stopRecording(); socket?.removeAllListeners(); socket?.disconnect(); };
}

export function watchLivePage({ sessionId, onEvents, onStatus, onMetrics }) {
  const controller = new AbortController();
  let socket, retryTimer, clockTimer, epoch = '', seq = 0, terminal = false;
  let offset = null, bestRtt = Infinity; const latencies = [];
  let bytes = 0, samples = 0, started = performance.now(), lastReport = started, firstFrame = null;
  const run = async () => {
    try {
      const connection = await connect(sessionId, null, controller.signal);
      if (controller.signal.aborted || !connection || terminal) return;
      socket = connection;
      const syncClock = () => {
        const before = Date.now();
        connection.timeout(1500).emit('live:clock', (error, time) => {
          if (error || !Number.isFinite(time)) return;
          const after = Date.now(), rtt = after - before;
          if (rtt <= bestRtt) { offset = (before + after) / 2 - time; bestRtt = rtt; }
        });
      };
      socket.on('connect', () => { epoch = ''; seq = 0; onStatus('connected'); socket.emit('live:resync'); syncClock(); });
      clockTimer = setInterval(syncClock, 10000);
      socket.on('live:batch', async (packet, reply) => {
        if (controller.signal.aborted) return;
        const full = packet.events.some(event => event.type === 2);
        if ((packet.epoch !== epoch && (!full || packet.seq !== 1)) ||
            (packet.epoch === epoch && packet.seq !== seq + 1)) {
          reply?.({ ok: false, reason: 'resync' }); socket.emit('live:resync'); return;
        }
        epoch = packet.epoch; seq = packet.seq;
        const began = performance.now();
        try {
          await onEvents(packet.events, { epoch, full });
          if (controller.signal.aborted) return;
          reply?.({ ok: true });
          bytes += eventBytes(packet); samples += packet.events.length;
          if (offset !== null) {
            latencies.push(Math.max(0, Date.now() - packet.events[packet.events.length - 1].timestamp - offset));
            if (latencies.length > 100) latencies.shift();
          }
          const sorted = [...latencies].sort((a, b) => a - b);
          if (firstFrame === null && full) firstFrame = performance.now() - started;
          if (performance.now() - lastReport > 1000) {
            onMetrics?.({ latencyP95: sorted.length ? Math.round(sorted[Math.ceil(sorted.length * .95) - 1]) : null, rtt: Number.isFinite(bestRtt) ? bestRtt : null, firstFrame: Math.round(firstFrame || 0), applyMs: Math.round(performance.now() - began),
              kbps: Math.round(bytes / (performance.now() - lastReport)), events: samples,
              heapMB: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1048576) : null });
            bytes = 0; samples = 0; lastReport = performance.now();
          }
        } catch (_) { reply?.({ ok: false, reason: 'resync' }); socket.emit('live:resync'); }
      });
      // Compatibility with the previous local server while it restarts.
      socket.on('live:events', events => { if (!controller.signal.aborted) onEvents(events); });
      socket.on('live:publisher-ready', () => { offset = null; bestRtt = Infinity; syncClock(); socket.emit('live:resync'); });
      socket.on('live:reset', () => { epoch = ''; seq = 0; onStatus('waiting'); socket.emit('live:resync'); });
      socket.on('live:peer-left', () => onStatus('waiting'));
      socket.on('live:ended', () => { terminal = true; onStatus('ended'); });
      const disconnected = () => {
        clearInterval(clockTimer); connection.removeAllListeners(); connection.disconnect();
        if (!controller.signal.aborted && !terminal) { onStatus('disconnected'); retryTimer = setTimeout(run, 750); }
      };
      socket.on('disconnect', disconnected); socket.on('connect_error', disconnected); socket.connect();
    } catch (_) {
      if (!controller.signal.aborted && !terminal) { onStatus('disconnected'); retryTimer = setTimeout(run, 1500); }
    }
  };
  run();
  const stop = () => { controller.abort(); clearInterval(clockTimer); clearTimeout(retryTimer); socket?.removeAllListeners(); socket?.disconnect(); };
  stop.point = id => new Promise(resolve => {
    if (!socket?.connected || !epoch) return resolve(false);
    socket.timeout(2000).emit('live:point', { id, epoch }, (error, result) => resolve(!error && result?.ok === true));
  });
  stop.resync = () => socket?.emit('live:resync');
  return stop;
}

// One authenticated control socket per document. HTTP is only a reconnect fallback.
export function observePresence({ getPresence, onUpdate }) {
  const controller = new AbortController();
  let socket, retry, heartbeat, refreshing = false;
  const refresh = async () => {
    if (refreshing || controller.signal.aborted) return;
    refreshing = true;
    try {
      let data;
      if (socket?.connected) {
        data = await new Promise((resolve, reject) => socket.timeout(2500).emit('live:presence', getPresence(),
          (error, response) => error || !response?.ok ? reject(error || new Error('presence')) : resolve(response.data)));
      } else {
        data = (await api.post('/api/v1/support-chat/presence', getPresence(), { signal: controller.signal })).data;
      }
      if (!controller.signal.aborted) onUpdate(data);
    } catch (_) { if (!controller.signal.aborted) onUpdate({ session: null }); }
    finally { refreshing = false; }
  };
  const run = async () => {
    try {
      const connection = await connect(null, null, controller.signal);
      if (controller.signal.aborted || !connection) return;
      socket = connection;
      connection.on('connect', refresh); connection.on('live:refresh', refresh);
      const reconnect = () => {
        connection.removeAllListeners(); connection.disconnect();
        if (!controller.signal.aborted) retry = setTimeout(run, 1500);
      };
      connection.on('disconnect', reconnect); connection.on('connect_error', reconnect); connection.connect();
    } catch (_) { if (!controller.signal.aborted) retry = setTimeout(run, 2000); }
  };
  run(); heartbeat = setInterval(refresh, 5000);
  const stop = () => { controller.abort(); clearTimeout(retry); clearInterval(heartbeat); socket?.removeAllListeners(); socket?.disconnect(); };
  stop.refresh = refresh;
  return stop;
}
